import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { trackClick } from '../utils/analytics';
import { Helmet } from 'react-helmet-async';
import { Award, Star, CheckCircle, ArrowRight, Fence, Trees, Building2, HardHat, DoorOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { VideoPlayer } from '../components/VideoPlayer';
import { toast } from 'sonner';
import axios from 'axios';

import { SiteHeader } from '../components/home/SiteHeader';
import { HeroSection } from '../components/home/HeroSection';
import { ContactSection } from '../components/home/ContactSection';
import { SiteFooter } from '../components/home/SiteFooter';
import { values, certifications, guarantees, testimonials, galleryImages } from '../data/homeData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceNav = [
  { title: 'Pipe Fencing', icon: Fence, href: '/pipe-fencing', desc: 'Heavy-duty 2-3/8" drill pipe fencing for ranches, farms, and rural properties.' },
  { title: 'Land Services', icon: Trees, href: '/land-services', desc: 'Land clearing, forestry mulching, dirt work, gravel, and site preparation.' },
  { title: 'Metal Buildings', icon: Building2, href: '/metal-buildings', desc: 'Custom shops, barns, and agricultural buildings — turnkey construction.' },
  { title: 'Concrete', icon: HardHat, href: '/concrete', desc: 'Driveways, shop slabs, patios, barn floors, and decorative concrete.' },
  { title: 'Gates & Fabrication', icon: DoorOpen, href: '/custom-gates', desc: 'Custom entry gates, ranch gates, automated systems, and steel work.' },
];

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', projectType: '', message: ''
  });

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name, email: formData.email || null, phone: formData.phone,
        address: formData.address || null, project_type: formData.projectType || null, message: formData.message
      });
      if (response.status === 200) {
        trackClick('Contact Form Submit', '/');
        toast.success('Thank you! We\'ll contact you soon for your free estimate.');
        setFormData({ name: '', email: '', phone: '', address: '', projectType: '', message: '' });
      }
    } catch {
      toast.error('Failed to submit form. Please try calling us directly at 817-723-5006.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>North Texas Welding & Fabrication | Built for the Texas Landowner</title>
        <meta name="description" content="Fence, land clearing, metal buildings, concrete, and custom gates across North Texas. One contractor for your entire property project. Springtown, Weatherford, Azle. 817-723-5006." />
        <link rel="canonical" href="https://ntxpipefence.com/" />
        <meta property="og:title" content="North Texas Welding & Fabrication | Built for the Texas Landowner" />
        <meta property="og:description" content="Fence, land clearing, metal buildings, concrete, and custom gates across North Texas. One contractor for your entire property project." />
        <meta property="og:url" content="https://ntxpipefence.com/" />
      </Helmet>

      <SiteHeader mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} scrollToSection={scrollToSection} />

      <HeroSection scrollToSection={scrollToSection} />

      {/* Service Navigation */}
      <section className="py-16 bg-white" id="services" data-testid="service-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-3">What We Build</h2>
            <p className="text-zinc-600 text-base max-w-2xl mx-auto">Professional property improvements for rural homeowners, ranchers, and acreage owners across North Texas.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {serviceNav.map(svc => {
              const Icon = svc.icon;
              return (
                <Link
                  key={svc.href}
                  to={svc.href}
                  onClick={() => trackClick(`Service Nav: ${svc.title}`, '/')}
                  className="group bg-zinc-50 border-2 border-zinc-100 rounded-xl p-6 hover:border-red-600 hover:shadow-lg transition-all text-center"
                  data-testid={`service-nav-${svc.href.slice(1)}`}
                >
                  <div className="w-14 h-14 bg-red-600 group-hover:bg-zinc-900 rounded-xl flex items-center justify-center mx-auto mb-4 transition-colors">
                    <Icon className="text-white" size={26} />
                  </div>
                  <h3 className="font-bold text-zinc-900 mb-2">{svc.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed mb-3">{svc.desc}</p>
                  <span className="text-red-600 text-sm font-semibold flex items-center justify-center gap-1 group-hover:gap-2 transition-all">
                    Learn More <ArrowRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* One Contractor Section */}
      <section className="py-16 bg-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzI2MjYyNiIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">One Contractor. One Property. Built Right.</h2>
            <p className="text-zinc-300 text-base max-w-3xl mx-auto leading-relaxed">
              From clearing raw acreage and preparing your site to pouring concrete, constructing your shop, installing your fence, and building your gates — North Texas Welding and Fabrication handles the property improvements Texas landowners need. Stop coordinating four different contractors. Hire one crew that does it all.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {['Raw Land', 'Land Clearing', 'Dirt Work', 'Gravel', 'Concrete', 'Metal Building', 'Fence', 'Gates'].map((step, i) => (
              <div key={step} className="flex items-center gap-2">
                <span className="bg-red-600/20 text-red-500 font-bold text-xs px-3 py-1.5 rounded-lg border border-red-600/30">
                  {step}
                </span>
                {i < 7 && <ArrowRight size={14} className="text-zinc-600" />}
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/instant-estimate" onClick={() => trackClick('Estimate - One Contractor', '/')}>
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-semibold text-lg px-8 py-6">
                Get an Instant Estimate <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-red-600 font-bold text-sm uppercase tracking-wider mb-3">About Us</h3>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">Locally Owned & Texas Proud</h2>
            <p className="text-zinc-600 text-base max-w-3xl mx-auto leading-relaxed">
              North Texas Welding and Fabrication, LLC is a locally owned property improvement contractor proudly serving
              Springtown, Weatherford, Azle, Decatur, Bridgeport, Fort Worth, and surrounding North Texas communities.
              Our welding and fabrication capabilities allow us to deliver a wider range of services than most contractors — from pipe fencing and custom gates to metal buildings and structural steel work.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img src="/images/quality-craftsmanship.jpg" alt="Quality Welding Work" className="rounded-lg shadow-2xl w-full" />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-zinc-900 mb-6">We Improve Texas Property</h3>
              <p className="text-zinc-700 mb-6 leading-relaxed">
                Whether you own 5 acres or 500, our team handles the property improvements that matter — fencing, land clearing, concrete, buildings, gates, and the welding and fabrication work that ties it all together. We are not a handyman service. We are skilled tradespeople who specialize in the heavy, permanent improvements that define Texas land.
              </p>
              <p className="text-zinc-700 mb-6 leading-relaxed">
                Every project is built with the same standards: clean welds, straight lines, quality materials, and professional installation built to last decades, not just years.
              </p>
              <div className="bg-zinc-50 border-l-4 border-red-600 p-6 rounded">
                <p className="text-zinc-900 font-semibold text-lg italic">
                  {`"I own acreage and these guys can handle most of what I need done."`}
                </p>
                <p className="text-zinc-500 text-sm mt-2">— What our customers say</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <Card key={value.title} className="border-zinc-200 hover:border-red-600 transition-all hover:shadow-lg">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-red-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-white" size={32} />
                    </div>
                    <h4 className="font-bold text-zinc-900 mb-2">{value.title}</h4>
                    <p className="text-zinc-600 text-sm">{value.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video Showcase */}
      <section className="py-20 bg-zinc-900" data-testid="video-showcase">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-red-500 font-bold text-sm uppercase tracking-wider mb-3">See Us In Action</h3>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Watch Our Work</h2>
            <p className="text-zinc-400 text-base max-w-2xl mx-auto">
              Fencing, welding, fabrication, and property improvements — built right across North Texas.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <VideoPlayer src="/images/promo-company-reel.mp4" title="North Texas Welding & Fabrication" />
            <VideoPlayer src="/images/promo-pipe-fence.mp4" title="NTX Pipe Fence — Built for Texas" />
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-white border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-red-600 font-bold text-sm uppercase tracking-wider mb-3">Licensed & Certified</h3>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">Trusted Professionals</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {certifications.map((cert) => (
              <Card key={cert.title} className="border-zinc-200 text-center hover:border-red-600 transition-all">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="text-white" size={32} />
                  </div>
                  <h4 className="font-bold text-zinc-900 mb-2">{cert.title}</h4>
                  <p className="text-zinc-600 text-sm">{cert.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="bg-zinc-50 rounded-2xl p-8 md:p-12">
            <h3 className="text-3xl font-bold text-zinc-900 mb-8 text-center">Our Service Guarantees</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {guarantees.map((guarantee) => (
                <div key={guarantee.title} className="flex items-start space-x-3">
                  <CheckCircle className="text-red-600 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-bold text-zinc-900 mb-1">{guarantee.title}</h4>
                    <p className="text-zinc-600 text-sm">{guarantee.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-red-600 font-bold text-sm uppercase tracking-wider mb-3">Client Reviews</h3>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">What Our Customers Say</h2>
            <p className="text-zinc-600 text-base">Real feedback from real North Texas property owners</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.name} className="border-zinc-200 hover:shadow-lg transition-all">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`${testimonial.name}-star-${i}`} className="text-red-600 fill-red-600" size={20} />
                    ))}
                  </div>
                  <p className="text-zinc-700 mb-6 leading-relaxed italic">{`"${testimonial.text}"`}</p>
                  <div className="border-t border-zinc-200 pt-4">
                    <p className="font-bold text-zinc-900">{testimonial.name}</p>
                    <p className="text-sm text-zinc-600">{testimonial.location}</p>
                    <p className="text-sm text-red-600 font-semibold mt-1">{testimonial.project}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-red-600 font-bold text-sm uppercase tracking-wider mb-3">Our Work</h3>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">Quality You Can See</h2>
            <p className="text-zinc-600 text-base max-w-3xl mx-auto">Recent fencing, welding, and property improvement projects across North Texas.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image) => (
              <div key={image.alt} className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all aspect-square">
                {image.type === 'video' ? (
                  <VideoPlayer src={image.url} title={image.title} aspectClass="aspect-square" className="h-full" />
                ) : (
                  <>
                    <img src={image.url} alt={image.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h4 className="text-white font-bold text-lg">{image.title}</h4>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financing */}
      <section className="py-16 bg-zinc-900" data-testid="homepage-financing">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-red-500 font-bold text-sm uppercase tracking-wider mb-4">Flexible Ways to Fund Your Project</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Build Now. Pay Over Time.</h2>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            Planning a new fence, shop, gate, concrete project, or property improvement? Flexible financing options are available for qualifying customers through Acorn Finance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/financing">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-bold text-lg py-6 px-10 w-full"
                onClick={() => trackClick('financing_from_homepage', '/')}
                data-testid="homepage-financing-cta"
              >
                CHECK MY FINANCING OPTIONS
              </Button>
            </Link>
            <Link to="/instant-estimate">
              <Button size="lg" variant="outline" className="border-2 border-zinc-600 text-zinc-300 hover:bg-white hover:text-black font-bold py-6 px-10 w-full"
                data-testid="homepage-financing-estimate"
              >
                GET AN INSTANT ESTIMATE
              </Button>
            </Link>
          </div>
          <p className="text-zinc-600 text-xs mt-6">Financing provided by third-party lenders through Acorn Finance. Subject to lender approval.</p>
        </div>
      </section>

      <ContactSection formData={formData} handleInputChange={handleInputChange} handleSubmit={handleSubmit} isSubmitting={isSubmitting} />

      <SiteFooter scrollToSection={scrollToSection} />
    </div>
  );
};

export default Home;
